/**
 * TemplatesList Component
 * Displays and manages project templates in a table view
 * Phase 7 Batch 7.3.4 - Templates Table View
 *
 * Templates are projects marked with is_template=true that can be used
 * to quickly create new proposals with pre-configured scopes and items.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects, useDeleteProject } from '@/hooks';
import { DuplicateProjectModal } from './DuplicateProjectModal';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, FileText, Trash2, AlertTriangle, Edit, FileCheck } from 'lucide-react';

export function TemplatesList() {
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');
  const [selectedTemplateNumber, setSelectedTemplateNumber] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch templates (projects where is_template=true)
  const { data, isLoading, error } = useProjects({ is_template: true });
  const deleteProject = useDeleteProject();

  const templates = data?.projects || [];

  const handleUseTemplate = (templateId: string, templateName: string, templateNumber: number) => {
    setSelectedTemplateId(templateId);
    setSelectedTemplateName(templateName);
    setSelectedTemplateNumber(templateNumber);
  };

  const handleCloseModal = () => {
    setSelectedTemplateId(null);
    setSelectedTemplateName('');
    setSelectedTemplateNumber(null);
  };

  const handleEditClick = (projectId: string) => {
    // Phase 7.3.3 Phase 5: Pass returnTo query param for context-aware navigation
    navigate(`/projects/${projectId}?returnTo=templates`);
  };

  const handleDeleteClick = (templateId: string) => {
    setDeleteConfirmId(templateId);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId) {
      await deleteProject.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 border border-destructive rounded-lg bg-destructive/10">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <p className="text-sm text-destructive">
          Failed to load templates. Please try again later.
        </p>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No Templates Found</h3>
        <p className="text-muted-foreground mb-4">
          Create your first template to quickly start new proposals with pre-configured scopes and items.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-center"># Scopes</TableHead>
              <TableHead className="text-center"># Items</TableHead>
              <TableHead className="text-right">Proposed Total</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{template.name}</span>
                    <span className="text-sm text-muted-foreground">#{template.project_number}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {template.scope_count ?? 0}
                </TableCell>
                <TableCell className="text-center">
                  {template.billable_item_count ?? 0}
                </TableCell>
                <TableCell className="text-right">
                  {template.proposed_total !== undefined && template.proposed_total > 0 ? (
                    <span className="font-semibold text-emerald-600">
                      ${template.proposed_total.toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">$0</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      onClick={() => handleUseTemplate(template.id, template.name, template.project_number)}
                      variant="outline"
                      size="sm"
                    >
                      <FileCheck className="h-4 w-4 mr-2" />
                      Use Template
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(template.id)}
                      aria-label="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(template.id)}
                      className="text-destructive hover:text-destructive"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Duplicate Project Modal (for creating from template) */}
      {selectedTemplateId && (
        <DuplicateProjectModal
          open={true}
          onClose={handleCloseModal}
          projectId={selectedTemplateId}
          projectName={selectedTemplateName}
          projectNumber={selectedTemplateNumber ?? undefined}
          isTemplate={true}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && handleCancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Template
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete this template?</p>
              <p className="font-medium text-destructive">
                Warning: This will also permanently delete all billable items and scopes associated with this template.
              </p>
              <p className="text-sm text-muted-foreground">
                Note: Templates are safe to delete as they don't contain real financial records.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
